/**
 * Module-level activity pub/sub for the BFF session-refresh timer (see useSessionTimer.ts).
 * Deliberately has no React dependency so any call site — a component handler, a plain
 * API-wrapper function, code that hasn't been converted to a hook — can import and call
 * notifySessionActivity() directly without needing to be inside the component tree that
 * mounts the timer.
 */

type ActivityListener = () => void;

const activityListeners = new Set<ActivityListener>();

/**
 * Call after a file upload, save, or submit action succeeds (or is attempted) so the
 * session timer resets its refresh cadence and immediately refreshes, rather than
 * waiting out the rest of the current interval. See docs/pretend-advisor.md and
 * ADR-0010 for the upload/save/submit call sites this is meant to guard.
 */
export function notifySessionActivity(): void {
  for (const listener of activityListeners) listener();
}

/** Internal — subscribed by useSessionTimer's effect. Returns an unsubscribe function. */
export function subscribeToSessionActivity(listener: ActivityListener): () => void {
  activityListeners.add(listener);
  return () => activityListeners.delete(listener);
}
